import React from 'react';
import { Users, Target, TrendingUp, Award, Lightbulb, Shield } from 'lucide-react';

interface LeadershipCompetency {
  name: string;
  score: number;
  description?: string;
  examples?: string[];
}

interface LeadershipCompetenciesProps {
  competencies: LeadershipCompetency[];
  title?: string;
}

const LeadershipCompetencies: React.FC<LeadershipCompetenciesProps> = ({
  competencies,
  title = 'Leadership Competencies Assessment'
}) => {
  const getIcon = (name: string) => {
    const nameLower = name.toLowerCase();
    if (nameLower.includes('team') || nameLower.includes('people')) return Users;
    if (nameLower.includes('strategic') || nameLower.includes('vision')) return Target;
    if (nameLower.includes('change') || nameLower.includes('transform')) return TrendingUp;
    if (nameLower.includes('decision') || nameLower.includes('judgment')) return Shield;
    if (nameLower.includes('innovation') || nameLower.includes('creative')) return Lightbulb;
    return Award;
  };

  const getScoreColor = (score: number) => {
    if (score >= 85) return {
      bg: 'bg-gradient-to-br from-green-500 to-emerald-600',
      border: 'border-green-500',
      text: 'text-green-700',
      light: 'bg-green-50',
      ring: 'ring-green-200'
    };
    if (score >= 70) return {
      bg: 'bg-gradient-to-br from-cyan-500 to-blue-600',
      border: 'border-cyan-500',
      text: 'text-cyan-700',
      light: 'bg-cyan-50',
      ring: 'ring-cyan-200'
    };
    if (score >= 50) return {
      bg: 'bg-gradient-to-br from-amber-500 to-orange-600',
      border: 'border-amber-500',
      text: 'text-amber-700',
      light: 'bg-amber-50',
      ring: 'ring-amber-200'
    };
    return {
      bg: 'bg-gradient-to-br from-gray-400 to-gray-500',
      border: 'border-gray-400',
      text: 'text-gray-700',
      light: 'bg-gray-50',
      ring: 'ring-gray-200'
    };
  };

  const sortedCompetencies = [...competencies].sort((a, b) => b.score - a.score);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-cyan-50">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600 mt-1">Evaluation of key leadership capabilities and management effectiveness</p>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedCompetencies.map((competency, index) => {
            const Icon = getIcon(competency.name);
            const colors = getScoreColor(competency.score);

            return (
              <div
                key={index}
                className={`${colors.light} border-2 ${colors.border} rounded-xl p-5 hover:shadow-lg transition-all duration-300 hover:scale-105`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`${colors.bg} p-3 rounded-lg shadow-md`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <div className={`flex items-center justify-center w-16 h-16 rounded-full ${colors.bg} text-white font-bold text-lg shadow-md ring-4 ${colors.ring}`}>
                    {competency.score}
                  </div>
                </div>

                <h4 className="font-semibold text-gray-900 mb-2 text-base">
                  {competency.name}
                </h4>

                {competency.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {competency.description}
                  </p>
                )}

                <div className="relative pt-2">
                  <div className="flex mb-2 items-center justify-between">
                    <div>
                      <span className={`text-xs font-semibold inline-block ${colors.text}`}>
                        {competency.score >= 85 ? 'Exceptional' : competency.score >= 70 ? 'Strong' : competency.score >= 50 ? 'Proficient' : 'Developing'}
                      </span>
                    </div>
                  </div>
                  <div className="overflow-hidden h-3 text-xs flex rounded-full bg-gray-200">
                    <div
                      style={{ width: `${competency.score}%` }}
                      className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${colors.bg} transition-all duration-500`}
                    />
                  </div>
                </div>

                {competency.examples && competency.examples.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <p className="text-xs font-medium text-gray-700 mb-2">Key Examples:</p>
                    <ul className="text-xs text-gray-600 space-y-1">
                      {competency.examples.slice(0, 2).map((example, idx) => (
                        <li key={idx} className="flex items-start gap-1">
                          <span className="text-blue-500 mt-0.5">•</span>
                          <span className="line-clamp-2">{example}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
        <div className="flex flex-wrap items-center justify-center gap-6 text-xs text-gray-600">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gradient-to-br from-green-500 to-emerald-600"></div>
            <span>Exceptional (85-100)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600"></div>
            <span>Strong (70-84)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gradient-to-br from-amber-500 to-orange-600"></div>
            <span>Proficient (50-69)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gradient-to-br from-gray-400 to-gray-500"></div>
            <span>Developing (&lt;50)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadershipCompetencies;
